(() => {
  if (window.__vccIntentLoaderReady) return;
  window.__vccIntentLoaderReady = true;

  const configNode = document.getElementById('VccDeferredComponentMap');
  let config = {};

  try {
    config = JSON.parse(configNode?.textContent || '{}');
  } catch (error) {
    console.warn('[VCC intent loader] Deferred component map could not be read.', error);
  }

  const sharedLoads = window.__vccIntentAssetLoads || {
    modules: new Map(),
    scripts: new Map(),
    styles: new Map(),
  };
  window.__vccIntentAssetLoads = sharedLoads;

  const moduleLoads = sharedLoads.modules;
  const scriptLoads = sharedLoads.scripts;
  const styleLoads = sharedLoads.styles;
  const replayBypass = new WeakSet();

  function loadModule(src) {
    if (!src) return Promise.resolve();
    if (moduleLoads.has(src)) return moduleLoads.get(src);

    // Dynamic import uses the browser's module map. It resolves immediately for
    // an already-evaluated foreign module and cannot miss a historic load event.
    const importer = window.__vccIntentImportModule || ((source) => import(source));
    const promise = Promise.resolve().then(() => importer(src)).then(() => undefined).catch((error) => {
      moduleLoads.delete(src);
      throw error;
    });

    moduleLoads.set(src, promise);
    return promise;
  }

  function scriptReady(src) {
    if (!src) return true;
    if (/vcc-wishlist-store\.js(?:\?|$)/.test(src)) return Boolean(window.VCCWishlist);
    if (/vcc-product-quick-actions\.js(?:\?|$)/.test(src)) return Boolean(window.__vccProductQuickActionsLoaded);
    if (/vcc-review-viewer\.js(?:\?|$)/.test(src)) return Boolean(window.VCCReviewViewer?.ready);
    return false;
  }

  function loadScript(src) {
    if (!src) return Promise.resolve();
    if (scriptReady(src)) return Promise.resolve();
    if (scriptLoads.has(src)) return scriptLoads.get(src);

    const promise = new Promise((resolve, reject) => {
      let existing = Array.from(document.scripts).find((script) => !script.type || script.type === 'text/javascript' ? script.src === src : false);
      if (existing?.dataset.vccIntentLoadFailed === 'true') {
        if (existing.dataset.vccIntentAsset === 'true') existing.remove();
        existing = null;
      }
      if (scriptReady(src) || existing?.dataset.vccLoaded === 'true') {
        resolve();
        return;
      }

      const script = existing || document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.vccIntentAsset = 'true';

      let readinessTimer = 0;
      let timeoutTimer = 0;
      const cleanup = () => {
        window.clearInterval(readinessTimer);
        window.clearTimeout(timeoutTimer);
      };
      const complete = () => {
        cleanup();
        script.dataset.vccLoaded = 'true';
        resolve();
      };
      const fail = (error) => {
        cleanup();
        script.dataset.vccIntentLoadFailed = 'true';
        if (script.dataset.vccIntentAsset === 'true') script.remove();
        reject(error instanceof Error ? error : new Error(`Script could not be loaded: ${src}`));
      };
      script.addEventListener('load', complete, { once: true });
      script.addEventListener('error', fail, { once: true });

      // A foreign defer/classic tag may have completed before this listener was
      // attached. Its public readiness marker is authoritative in that case.
      if (existing) {
        readinessTimer = window.setInterval(() => {
          if (scriptReady(src)) complete();
        }, 16);
        timeoutTimer = window.setTimeout(() => {
          if (scriptReady(src)) complete();
          else fail(new Error(`Existing script did not expose readiness: ${src}`));
        }, 5000);
      }
      if (!existing) document.head.appendChild(script);
    }).catch((error) => {
      scriptLoads.delete(src);
      throw error;
    });

    scriptLoads.set(src, promise);
    return promise;
  }

  function loadStyle(src) {
    if (!src) return Promise.resolve();
    if (styleLoads.has(src)) return styleLoads.get(src);

    const promise = new Promise((resolve, reject) => {
      let existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((link) => link.href === src);
      if (existing?.dataset.vccIntentLoadFailed === 'true') {
        if (existing.dataset.vccIntentAsset === 'true') existing.remove();
        existing = null;
      }
      if (existing?.sheet) {
        resolve();
        return;
      }

      const link = existing || document.createElement('link');
      link.rel = 'stylesheet';
      link.href = src;
      link.dataset.vccIntentAsset = 'true';
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', (error) => {
        link.dataset.vccIntentLoadFailed = 'true';
        if (link.dataset.vccIntentAsset === 'true') link.remove();
        reject(error);
      }, { once: true });
      if (!existing) document.head.appendChild(link);
    }).catch((error) => {
      styleLoads.delete(src);
      throw error;
    });

    styleLoads.set(src, promise);
    return promise;
  }

  function executeScript(src) {
    if (!src) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.vccIntentAsset = 'true';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  }

  function loadScriptsInOrder(sources = []) {
    return sources.filter(Boolean).reduce((promise, src) => promise.then(() => loadScript(src)), Promise.resolve());
  }

  function loadModules(sources = []) {
    return Promise.all(sources.filter(Boolean).map(loadModule));
  }

  function waitForDefinitions(names = []) {
    return Promise.all(names.filter(Boolean).map((name) => (
      customElements.get(name) ? Promise.resolve() : customElements.whenDefined(name)
    )));
  }

  const ownerDescriptors = [...(config.components || []), ...(config.owners || [])]
    .filter((owner) => owner?.selector && owner?.src);
  const ownerLoads = new Map();
  const ownerReady = new Set();
  const observedOwners = new WeakMap();
  const heroOwners = new Set();
  const localizationSelects = new WeakSet();
  const viewportObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        viewportObserver.unobserve(entry.target);
        const owners = observedOwners.get(entry.target);
        owners?.forEach((owner) => loadOwner(owner));
        observedOwners.delete(entry.target);
      });
    }, { rootMargin: '700px 0px' })
    : null;

  function isFirstHeroPainted() {
    return Boolean(
      window.__vccFirstHeroPainted
      || window.VCCFirstHeroPaint?.painted
      || document.documentElement.dataset.vccFirstHeroPainted === 'true'
    );
  }

  function ownerNodes(root, selector) {
    if (!(root instanceof Element || root instanceof Document || root instanceof DocumentFragment)) return [];
    const nodes = [];
    if (root instanceof Element && root.matches(selector)) nodes.push(root);
    root.querySelectorAll(selector).forEach((node) => nodes.push(node));
    return nodes;
  }

  function loadOwner(owner) {
    if (!owner?.src) return Promise.resolve();
    if (ownerLoads.has(owner)) return ownerLoads.get(owner);
    const promise = (owner.script ? loadScript(owner.src) : loadModule(owner.src))
      .then(() => {
        ownerReady.add(owner);
      })
      .catch((error) => {
        ownerLoads.delete(owner);
        console.warn(`[VCC intent loader] ${owner.selector} could not be initialized.`, error);
        throw error;
      });
    ownerLoads.set(owner, promise);
    return promise;
  }

  function observeOwnerNode(node, owner) {
    if (owner.mode === 'hero') {
      if (isFirstHeroPainted()) loadOwner(owner).catch(() => {});
      else heroOwners.add(owner);
      return;
    }
    if (owner.mode === 'intent') return;
    if (!viewportObserver) return;
    const owners = observedOwners.get(node) || new Set();
    if (owners.has(owner)) return;
    owners.add(owner);
    observedOwners.set(node, owners);
    viewportObserver.observe(node);
  }

  function discoverOwners(root = document) {
    ownerDescriptors.forEach((owner) => {
      ownerNodes(root, owner.selector).forEach((node) => observeOwnerNode(node, owner));
    });
    discoverLocalization(root);
  }

  function releaseHeroOwners() {
    if (!isFirstHeroPainted()) return;
    heroOwners.forEach((owner) => loadOwner(owner).catch(() => {}));
    heroOwners.clear();
  }

  document.addEventListener('vcc:first-hero-painted', releaseHeroOwners, { once: true });

  const quickAddPromise = { current: null };
  const nativeSearchModulesPromise = { current: null };
  const quickActionsPromise = { current: null };
  const wishlistPromise = { current: null };
  const endpointMarkupLoads = new Map();
  const nativeSearchMarkupLoads = new WeakMap();
  let nativeSearchActivation = null;

  function loadQuickAdd() {
    if (!quickAddPromise.current) {
      quickAddPromise.current = Promise.all([
        loadStyle(config.quickAdd?.style),
        loadModules(config.quickAdd?.modules),
      ])
        .then(() => waitForDefinitions(config.quickAdd?.definitions))
        .catch((error) => {
          quickAddPromise.current = null;
          throw error;
        });
    }
    return quickAddPromise.current;
  }

  function quickAddReady() {
    return (config.quickAdd?.definitions || []).filter(Boolean).every((name) => customElements.get(name));
  }

  function sameOriginUrl(value) {
    const url = new URL(value, window.location.href);
    if (url.origin !== window.location.origin) throw new Error('A deferred endpoint was not same-origin.');
    return url.href;
  }

  function fetchApplicationTemplate(endpoint, selector) {
    const url = sameOriginUrl(endpoint);
    const key = `${url}::${selector}`;
    if (endpointMarkupLoads.has(key)) return endpointMarkupLoads.get(key);
    const promise = fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    }).then((response) => {
      if (!response.ok) throw new Error(`Endpoint returned ${response.status}.`);
      return response.text();
    }).then((markup) => {
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const application = parsed.querySelector(selector);
      if (!application) throw new Error(`Endpoint omitted ${selector}.`);
      const template = document.createElement('template');
      template.content.append(application.cloneNode(true));
      return template;
    }).catch((error) => {
      endpointMarkupLoads.delete(key);
      throw error;
    });
    endpointMarkupLoads.set(key, promise);
    return promise;
  }

  function currentNativeSearchBootstrap() {
    return document.querySelector(config.nativeSearch?.bootstrap || '[data-vcc-native-search-bootstrap]');
  }

  function materializeNativeSearch(bootstrap = currentNativeSearchBootstrap()) {
    const existing = document.getElementById('search-modal');
    if (existing) return Promise.resolve(existing);
    const endpoint = bootstrap?.dataset.vccNativeSearchEndpoint;
    if (!bootstrap || !endpoint) return Promise.reject(new Error('Native search endpoint is unavailable.'));
    if (nativeSearchMarkupLoads.has(bootstrap)) return nativeSearchMarkupLoads.get(bootstrap);
    const promise = fetchApplicationTemplate(endpoint, '[data-vcc-native-search-application]').then((template) => {
      if (!bootstrap.isConnected) throw new Error('Native search owner was replaced before mounting.');
      const mounted = document.getElementById('search-modal');
      if (mounted) return mounted;
      const application = template.content.firstElementChild?.cloneNode(true);
      if (!(application instanceof Element)) throw new Error('Native search application could not be cloned.');
      bootstrap.after(application);
      const materialized = application.querySelector('#search-modal');
      if (!materialized) throw new Error('Native search dialog could not be materialized.');
      return materialized;
    }).catch((error) => {
      nativeSearchMarkupLoads.delete(bootstrap);
      throw error;
    });
    nativeSearchMarkupLoads.set(bootstrap, promise);
    return promise;
  }

  function loadNativeSearch(bootstrap = currentNativeSearchBootstrap()) {
    if (!nativeSearchModulesPromise.current) {
      nativeSearchModulesPromise.current = loadModules(config.nativeSearch?.modules)
        .then(() => waitForDefinitions(config.nativeSearch?.definitions))
        .catch((error) => {
          nativeSearchModulesPromise.current = null;
          throw error;
        });
    }
    // Markup belongs to the replaceable header section. Fetch and module work
    // run together, while the endpoint payload itself remains shared/deduped.
    return Promise.all([nativeSearchModulesPromise.current, materializeNativeSearch(bootstrap)])
      .then(() => {
        if (!nativeSearchReady()) throw new Error('Native search did not become ready.');
      });
  }

  function nativeSearchReady() {
    return Boolean(
      document.getElementById('search-modal')
      && document.querySelector('style[data-vcc-native-search-style]')
      && customElements.get('dialog-component')
      && customElements.get('predictive-search-component')
    );
  }

  function openNativeSearch(opener) {
    const dialog = document.getElementById('search-modal');
    if (!dialog || typeof dialog.showDialog !== 'function') throw new Error('Native search dialog is unavailable.');
    dialog.showDialog();
    if (opener instanceof HTMLElement) dialog.dataset.vccIntentOpener = opener.id || '';
  }

  function activateNativeSearch(opener) {
    const bootstrap = currentNativeSearchBootstrap();
    if (nativeSearchActivation?.bootstrap === bootstrap && bootstrap?.isConnected !== false) {
      return nativeSearchActivation.promise;
    }
    const readiness = nativeSearchReady() ? Promise.resolve() : loadNativeSearch(bootstrap);
    const activation = { bootstrap, promise: null };
    activation.promise = readiness
      .then(() => {
        // A Shopify section replacement can supersede this activation while
        // its shared modules are still loading. Only the latest live owner may
        // open the newly-materialized dialog.
        if (
          nativeSearchActivation !== activation
          || bootstrap?.isConnected === false
          || opener?.isConnected === false
        ) return;
        // Replay the original semantic control once after the fetched
        // application and custom elements exist, then open the dialog. The
        // replay bypass prevents this capture loader from recursing.
        replay(opener);
        // A future/native opener handler may schedule showDialog from the
        // replay. Inspect on its frame before using the explicit fallback so
        // two requestAnimationFrame callbacks can never queue showModal twice.
        return new Promise((resolve) => window.requestAnimationFrame(() => {
          const nativeDialog = document.querySelector('#search-modal [ref="dialog"], #search-modal dialog');
          if (!nativeDialog?.open) openNativeSearch(opener);
          window.requestAnimationFrame(resolve);
        }));
      })
      .finally(() => {
        if (nativeSearchActivation === activation) nativeSearchActivation = null;
      });
    nativeSearchActivation = activation;
    return activation.promise;
  }

  function loadQuickActions() {
    if (!quickActionsPromise.current) {
      quickActionsPromise.current = loadScriptsInOrder(config.quickActions?.scripts).catch((error) => {
        quickActionsPromise.current = null;
        throw error;
      });
    }
    return quickActionsPromise.current;
  }

  function loadWishlist() {
    if (!wishlistPromise.current) {
      wishlistPromise.current = loadScriptsInOrder(config.wishlist?.scripts).catch((error) => {
        wishlistPromise.current = null;
        throw error;
      });
    }
    return wishlistPromise.current;
  }

  let reviewLoadPromise = null;
  let reviewLoadBootstrap = null;

  function reviewBootstrapFor(opener) {
    const selector = '[data-vcc-review-bootstrap]';
    const productOwner = opener instanceof Element
      ? opener.closest('[id^="VccProductInfoV2-"]')
      : null;
    const productInstance = productOwner?.id.slice('VccProductInfoV2-'.length);
    const scope = opener instanceof Element
      ? opener.closest('[id^="shopify-section-"], section, [data-vcc-product-information]')
      : null;
    const scoped = scope ? Array.from(scope.querySelectorAll(selector)) : [];
    const all = Array.from(document.querySelectorAll(selector));
    return all.find((node) => productInstance && node.dataset.vccReviewInstance === productInstance)
      || scoped.find((node) => node.querySelector('[data-vcc-review-style]'))
      || all.find((node) => node.querySelector('[data-vcc-review-style]'))
      || scoped[0]
      || all[0];
  }

  function readJson(node, fallback = {}) {
    if (!node) return { ...fallback };
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return { ...fallback };
    }
  }

  function remapApplicationIds(application, instance) {
    const safeInstance = String(instance || 'global').replace(/[^a-zA-Z0-9_-]/g, '-');
    const ids = new Map();
    application.querySelectorAll('[id]').forEach((node) => {
      const oldId = node.id;
      const newId = oldId.includes('endpoint')
        ? oldId.replaceAll('endpoint', safeInstance)
        : `${oldId}-${safeInstance}`;
      node.id = newId;
      ids.set(oldId, newId);
    });
    ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for', 'popovertarget'].forEach((attribute) => {
      application.querySelectorAll(`[${attribute}]`).forEach((node) => {
        const value = node.getAttribute(attribute);
        if (!value) return;
        node.setAttribute(attribute, value.split(/\s+/).map((token) => ids.get(token) || token).join(' '));
      });
    });
  }

  function patchReviewApplication(application, bootstrap) {
    const overlay = application.querySelector('[data-vcc-review-overlay]');
    const viewer = application.querySelector('[data-vcc-review-viewer]');
    const configNode = application.querySelector('[data-vcc-review-config]');
    if (!overlay || !viewer || !configNode) throw new Error('Review endpoint application is incomplete.');

    const instance = bootstrap.dataset.vccReviewInstance || 'global';
    remapApplicationIds(application, instance);
    overlay.dataset.vccReviewInstance = instance;
    viewer.dataset.vccReviewTheme = bootstrap.dataset.vccReviewTheme || viewer.dataset.vccReviewTheme || 'dark';
    viewer.dataset.vccReviewThemeBehavior = bootstrap.dataset.vccReviewThemeBehavior
      || viewer.dataset.vccReviewThemeBehavior
      || 'follow-site';

    const styleMarker = bootstrap.querySelector('[data-vcc-review-style]');
    if (styleMarker?.hasAttribute('style')) viewer.setAttribute('style', styleMarker.getAttribute('style'));

    const endpointConfig = readJson(configNode);
    const bootstrapConfigNode = bootstrap.querySelector('[data-vcc-review-bootstrap-config]');
    const reviewConfig = bootstrapConfigNode ? readJson(bootstrapConfigNode, endpointConfig) : endpointConfig;
    reviewConfig.defaultRating = bootstrap.dataset.vccReviewRating || reviewConfig.defaultRating || '4.96';
    reviewConfig.defaultCount = Number(bootstrap.dataset.vccReviewCount || reviewConfig.defaultCount || 526);
    reviewConfig.themeBehavior = bootstrap.dataset.vccReviewThemeBehavior || reviewConfig.themeBehavior || 'follow-site';
    configNode.textContent = JSON.stringify(reviewConfig);

    const eyebrow = application.querySelector('.vcc-review-header__eyebrow');
    const heading = application.querySelector('.vcc-review-header h2');
    const description = application.querySelector('.vcc-review-header p');
    const average = application.querySelector('[data-vcc-average]');
    const stars = application.querySelector('.vcc-review-score__stars');
    const loading = application.querySelector('[data-vcc-review-loading]');
    const themeToggle = application.querySelector('[data-vcc-theme-toggle]');
    const commentNote = application.querySelector('[data-vcc-comment-note]');
    if (eyebrow) eyebrow.textContent = bootstrap.dataset.vccReviewEyebrow || eyebrow.textContent;
    if (heading) heading.textContent = bootstrap.dataset.vccReviewHeading || heading.textContent;
    if (description) description.textContent = bootstrap.dataset.vccReviewDescription || description.textContent;
    if (average) average.textContent = String(reviewConfig.defaultRating);
    if (stars) stars.setAttribute('aria-label', `Average rating ${reviewConfig.defaultRating} out of 5`);
    if (loading) loading.textContent = `Loading ${reviewConfig.defaultCount} customer reviews…`;
    if (themeToggle) {
      themeToggle.hidden = !(reviewConfig.themeBehavior === 'independent' && reviewConfig.allowThemeSwitch);
    }
    if (commentNote) {
      commentNote.hidden = reviewConfig.commentNotePlacement !== 'toolbar';
      commentNote.textContent = reviewConfig.defaultHideEmpty
        ? 'Reviews without comments are hidden.'
        : 'Reviews without comments can appear.';
    }
    application.querySelectorAll('[data-vcc-marquee-row]').forEach((row) => {
      const direction = row.dataset.vccMarqueeRow === '1'
        ? reviewConfig.topDirection
        : reviewConfig.bottomDirection;
      if (direction) row.dataset.direction = direction;
    });
    return overlay;
  }

  function materializeReviewViewer(bootstrap) {
    const existing = document.querySelector('[data-vcc-review-overlay]');
    if (existing) return Promise.resolve(existing);
    const endpoint = bootstrap?.dataset.vccReviewEndpoint;
    if (!bootstrap || !endpoint) return Promise.reject(new Error('Review viewer endpoint is unavailable.'));
    return fetchApplicationTemplate(endpoint, '[data-vcc-review-application]').then((template) => {
      if (!bootstrap.isConnected) throw new Error('Review viewer owner was replaced before mounting.');
      const application = template.content.firstElementChild?.cloneNode(true);
      if (!(application instanceof Element)) throw new Error('Review viewer application could not be cloned.');
      const overlay = patchReviewApplication(application, bootstrap);
      bootstrap.after(application);
      return overlay;
    });
  }

  function loadReviewViewer(opener) {
    const bootstrap = reviewBootstrapFor(opener);
    if (!bootstrap) return Promise.reject(new Error('Review viewer bootstrap is unavailable.'));
    const desiredInstance = bootstrap.dataset.vccReviewInstance || 'global';
    const mountedInstance = window.VCCReviewViewer?.overlay?.dataset.vccReviewInstance;
    if (window.VCCReviewViewer?.ready && mountedInstance === desiredInstance) return Promise.resolve();
    if (window.VCCReviewViewer?.ready && mountedInstance !== desiredInstance) {
      window.VCCReviewViewer.destroy?.();
    }
    if (reviewLoadPromise) {
      if (reviewLoadBootstrap === bootstrap) return reviewLoadPromise;
      return reviewLoadPromise.catch(() => {}).then(() => loadReviewViewer(opener));
    }
    reviewLoadBootstrap = bootstrap;
    const promise = Promise.all([
      materializeReviewViewer(bootstrap),
      loadStyle(bootstrap.dataset.vccReviewCss),
    ])
      .then(() => executeScript(bootstrap.dataset.vccReviewScript))
      .then(() => {
        if (!window.VCCReviewViewer?.ready) throw new Error('Review viewer runtime did not become ready.');
      })
      .finally(() => {
        if (reviewLoadPromise === promise) {
          reviewLoadPromise = null;
          reviewLoadBootstrap = null;
        }
      });
    reviewLoadPromise = promise;
    return reviewLoadPromise;
  }

  const localizationShellLoads = new WeakMap();
  let localizationPayloadPromise = null;
  const localizationViewportObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        localizationViewportObserver.unobserve(entry.target);
        hydrateLocalizationSelect(entry.target).catch(() => {});
      });
    }, { rootMargin: '800px 0px' })
    : null;

  function localizationEndpoint(owner) {
    return owner?.closest?.('[data-vcc-localization-shell]')?.dataset.vccLocalizationEndpoint
      || config.localization?.endpoint
      || document.querySelector('[data-vcc-localization-endpoint]')?.dataset.vccLocalizationEndpoint;
  }

  function loadLocalizationPayload(owner) {
    if (localizationPayloadPromise) return localizationPayloadPromise;
    const endpoint = localizationEndpoint(owner);
    if (!endpoint) return Promise.reject(new Error('Localization endpoint is unavailable.'));
    localizationPayloadPromise = fetchApplicationTemplate(endpoint, '[data-vcc-localization-payload]')
      .then((template) => {
        const payload = template.content.firstElementChild;
        const recordsTemplate = payload?.querySelector('template[data-vcc-localization-records]');
        if (!(payload instanceof Element) || !(recordsTemplate instanceof HTMLTemplateElement)) {
          throw new Error('Localization endpoint payload is incomplete.');
        }
        return {
          currentCountry: payload.dataset.currentCountry || '',
          currentCurrency: payload.dataset.currentCurrency || '',
          records: Array.from(recordsTemplate.content.querySelectorAll('[data-vcc-localization-record]')),
        };
      })
      .catch((error) => {
        localizationPayloadPromise = null;
        throw error;
      });
    return localizationPayloadPromise;
  }

  function localizationGroup(panelId, suffix, label, records) {
    const group = document.createElement('div');
    const labelId = `${panelId}-${suffix}`;
    group.className = 'vcc-country-picker__group';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', labelId);
    const heading = document.createElement('div');
    heading.className = 'vcc-country-picker__group-label';
    heading.id = labelId;
    heading.textContent = label;
    group.append(heading, ...records.map((record) => record.cloneNode(true)));
    return group;
  }

  function buildLocalizationPanel(shell, payload) {
    const panelId = shell.dataset.vccLocalizationPopoverId;
    const placement = shell.dataset.vccLocalizationPlacement || 'below';
    const trigger = shell.querySelector('[data-vcc-localization-trigger]');
    if (!panelId || !trigger) throw new Error('Localization shell is incomplete.');

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = `vcc-country-picker__panel vcc-country-picker__panel--${placement}`;
    panel.setAttribute('ref', 'popover');
    panel.setAttribute('popover', 'auto');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Change country or region');
    panel.dataset.vccLocalizationPopover = '';
    const anchorName = trigger.style.getPropertyValue('anchor-name');
    if (anchorName) panel.style.setProperty('position-anchor', anchorName);

    const heading = document.createElement('div');
    heading.className = 'vcc-country-picker__heading';
    const headingText = document.createElement('span');
    headingText.textContent = 'Change country/region';
    const close = document.createElement('button');
    close.className = 'vcc-country-picker__close';
    close.type = 'button';
    close.setAttribute('popovertarget', panelId);
    close.setAttribute('popovertargetaction', 'hide');
    close.setAttribute('aria-label', 'Close country and currency menu');
    close.innerHTML = '<span aria-hidden="true">&times;</span>';
    heading.append(headingText, close);

    const scroll = document.createElement('div');
    scroll.className = 'vcc-country-picker__scroll';
    const priorityOrder = ['US', 'CA', 'GB', 'AU', 'DE', 'JP', 'NZ', 'FR', 'IT', 'ES', 'NL', 'SE', 'MX', 'SG', 'KR'];
    const shortcuts = payload.records
      .filter((record) => record.dataset.priority === 'true')
      .sort((a, b) => priorityOrder.indexOf(a.value) - priorityOrder.indexOf(b.value));
    scroll.append(
      localizationGroup(panelId, 'shortcuts', 'Country shortcuts', shortcuts),
      localizationGroup(panelId, 'all', 'All countries/regions', payload.records),
    );
    panel.append(heading, scroll);
    panel.addEventListener('toggle', (event) => {
      const open = event.newState ? event.newState === 'open' : panel.matches(':popover-open');
      trigger.setAttribute('aria-expanded', String(open));
    });
    shell.append(panel);
    return panel;
  }

  function upgradeLocalizationShell(shell) {
    if (shell.localName === 'anchored-popover-component') return shell;
    const upgraded = document.createElement('anchored-popover-component');
    Array.from(shell.attributes).forEach((attribute) => upgraded.setAttribute(attribute.name, attribute.value));
    while (shell.firstChild) upgraded.append(shell.firstChild);
    shell.replaceWith(upgraded);
    return upgraded;
  }

  function ensureLocalizationShell(shell) {
    if (!(shell instanceof Element)) return Promise.reject(new Error('Localization shell is unavailable.'));
    if (shell.dataset.vccLocalizationReady === 'true') return Promise.resolve(shell);
    if (localizationShellLoads.has(shell)) return localizationShellLoads.get(shell);
    const polyfill = 'showPopover' in HTMLElement.prototype
      ? Promise.resolve()
      : loadScript(config.localization?.polyfill);
    const assets = Promise.all([
      loadStyle(config.localization?.style),
      polyfill,
      loadModule(config.localization?.anchoredPopover),
    ]);
    const promise = Promise.all([loadLocalizationPayload(shell), assets])
      .then(([payload]) => {
        if (!shell.isConnected) throw new Error('Localization shell was replaced before mounting.');
        if (!shell.querySelector('[data-vcc-localization-popover]')) buildLocalizationPanel(shell, payload);
      })
      .then(() => waitForDefinitions(['anchored-popover-component']))
      .then(() => {
        if (!shell.isConnected) throw new Error('Localization shell was replaced before upgrade.');
        const upgraded = upgradeLocalizationShell(shell);
        upgraded.dataset.vccLocalizationReady = 'true';
        return upgraded;
      })
      .catch((error) => {
        localizationShellLoads.delete(shell);
        throw error;
      });
    localizationShellLoads.set(shell, promise);
    return promise;
  }

  function activateLocalization(trigger) {
    const shell = trigger.closest('[data-vcc-localization-shell]');
    return ensureLocalizationShell(shell).then((activeShell) => {
      const activeTrigger = activeShell.querySelector('[data-vcc-localization-trigger]');
      const panel = activeShell.querySelector('[data-vcc-localization-popover]');
      if (!(panel instanceof HTMLElement) || typeof panel.showPopover !== 'function') {
        throw new Error('Localization popover is unavailable.');
      }
      if (!panel.matches(':popover-open')) panel.showPopover();
      activeTrigger?.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        panel.querySelector('[aria-current="true"], .vcc-country-picker__option')?.focus();
      });
    });
  }

  function hydrateLocalizationSelect(select) {
    if (!(select instanceof HTMLSelectElement) || select.dataset.vccLocalizationHydrated === 'true') {
      return Promise.resolve();
    }
    return loadLocalizationPayload(select).then((payload) => {
      if (!select.isConnected) return;
      const options = payload.records.map((record) => {
        const option = document.createElement('option');
        const name = record.querySelector('span')?.textContent?.trim() || record.value;
        const currency = record.querySelector('strong')?.textContent?.trim() || '';
        option.value = record.value;
        option.textContent = currency ? `${name} · ${currency}` : name;
        option.selected = record.getAttribute('aria-current') === 'true';
        return option;
      });
      select.replaceChildren(...options);
      select.dataset.vccLocalizationHydrated = 'true';
    });
  }

  function discoverLocalization(root = document) {
    ownerNodes(root, '[data-vcc-localization-select]').forEach((select) => {
      if (localizationSelects.has(select)) return;
      localizationSelects.add(select);
      localizationViewportObserver?.observe(select);
    });
  }

  function clickableControl(target) {
    if (!(target instanceof Element)) return null;
    return target.closest('button, a, input, summary, [role="button"], [tabindex]') || target;
  }

  function replay(control) {
    if (!(control instanceof Element) || !control.isConnected) return;
    replayBypass.add(control);
    if (typeof control.click === 'function') control.click();
    else control.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }

  function intercept(event, control, promise, label, failureFallback) {
    if (!control || replayBypass.has(control)) {
      if (control) replayBypass.delete(control);
      return false;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    promise
      .then(() => replay(control))
      .catch((error) => {
        console.warn(`[VCC intent loader] ${label} could not be initialized.`, error);
        if (typeof failureFallback === 'function') failureFallback(control);
        else replay(control);
      });
    return true;
  }

  function matches(target, selector) {
    return target instanceof Element && selector && Boolean(target.closest(selector));
  }

  function warmFromIntent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (matches(target, config.nativeSearch?.selector)) loadNativeSearch().catch(() => {});
    if (matches(target, config.quickAdd?.selector)) loadQuickAdd().catch(() => {});
    if (matches(target, config.quickActions?.selector)) loadQuickActions().catch(() => {});
    if (matches(target, config.wishlist?.selector)) loadWishlist().catch(() => {});
    if (event.type !== 'pointerover' && target.closest('[data-vcc-open-reviews]')) {
      loadReviewViewer(target).catch(() => {});
    }
    const localizationTrigger = target.closest('[data-vcc-localization-trigger]');
    if (localizationTrigger) {
      const shell = localizationTrigger.closest('[data-vcc-localization-shell]');
      ensureLocalizationShell(shell).catch(() => {});
    }
    const localizationSelect = target.closest('[data-vcc-localization-select]');
    if (localizationSelect) hydrateLocalizationSelect(localizationSelect).catch(() => {});
    ownerDescriptors.forEach((owner) => {
      if (target.closest(owner.selector)) loadOwner(owner).catch(() => {});
    });
  }

  ['pointerover', 'pointerdown', 'touchstart', 'focusin'].forEach((eventName) => {
    document.addEventListener(eventName, warmFromIntent, { capture: true, passive: true });
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const clickedControl = clickableControl(target);
    if (clickedControl && replayBypass.has(clickedControl)) {
      replayBypass.delete(clickedControl);
      return;
    }

    const localizationTrigger = target.closest('[data-vcc-localization-trigger]');
    if (localizationTrigger && !localizationTrigger.closest('[data-vcc-localization-shell]')
      ?.querySelector('[data-vcc-localization-popover]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      activateLocalization(localizationTrigger).catch((error) => {
        console.warn('[VCC intent loader] Localization picker could not be initialized.', error);
      });
      return;
    }

    const reviewTrigger = target.closest('[data-vcc-open-reviews]');
    const reviewBootstrap = reviewTrigger ? reviewBootstrapFor(reviewTrigger) : null;
    const reviewInstanceMatches = Boolean(
      window.VCCReviewViewer?.ready
      && reviewBootstrap
      && window.VCCReviewViewer.overlay?.dataset.vccReviewInstance === reviewBootstrap.dataset.vccReviewInstance
    );
    if (reviewTrigger && !reviewInstanceMatches) {
      intercept(event, reviewTrigger, loadReviewViewer(reviewTrigger), 'Review viewer');
      return;
    }

    if (matches(target, config.nativeSearch?.selector)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const opener = clickableControl(target);
      activateNativeSearch(opener)
        .catch((error) => {
          console.warn('[VCC intent loader] Native search could not be initialized.', error);
          window.location.assign(window.Theme?.routes?.search_url || '/search');
        });
      return;
    }

    if (matches(target, config.quickActions?.selector) && !window.__vccProductQuickActionsLoaded) {
      intercept(event, clickableControl(target), loadQuickActions(), 'Product quick actions');
      return;
    }

    if (matches(target, config.quickAdd?.selector) && !quickAddReady()) {
      intercept(event, clickableControl(target), loadQuickAdd(), 'Quick add');
      return;
    }

    if (matches(target, config.wishlist?.selector) && !window.VCCWishlist) {
      intercept(event, clickableControl(target), loadWishlist(), 'Wishlist');
      return;
    }

    const coldOwner = ownerDescriptors.find((owner) => (
      !ownerReady.has(owner) && target.closest(owner.selector)
    ));
    if (coldOwner) {
      intercept(event, clickableControl(target), loadOwner(coldOwner), coldOwner.selector);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (!event.metaKey || event.key.toLowerCase() !== 'k') return;
    const opener = document.querySelector(config.nativeSearch?.selector || '[data-vcc-native-search-open]');
    if (!opener || nativeSearchReady()) return;
    event.preventDefault();
    activateNativeSearch(opener)
      .catch(() => window.location.assign(window.Theme?.routes?.search_url || '/search'));
  }, true);

  const startOwnerScan = () => {
    discoverOwners(document);
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element || node instanceof DocumentFragment) discoverOwners(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startOwnerScan, { once: true });
  } else {
    startOwnerScan();
  }

  window.VCCIntentLoader = Object.freeze({
    loadModule,
    loadScript,
    loadQuickAdd,
    loadNativeSearch,
    loadQuickActions,
    loadWishlist,
    loadReviewViewer,
    nativeSearchReady,
    quickAddReady,
    activateNativeSearch,
    loadLocalizationPayload,
    ensureLocalizationShell,
    hydrateLocalizationSelect,
  });
})();
